import { FC } from 'react';
import styled from 'styled-components';
import { Icon } from '../Icon/Icon';
import { Grid, IColumnWidthProps } from '../Grid/Grid';
import vars from '../theme/vars';
import { AccessibleRoleProp } from './List';

const ListItemElement = styled.div<IProps>`
  word-break: break-word;
  display: flex;
  position: relative;
  padding: 10px 20px;
  align-items: center;
  ${({ variation, theme }) =>
    variation !== 'clean' &&
    variation !== 'full-border' &&
    `
  border-bottom: 1px solid ${theme.vars.newColor.lightGS1};
  &:first-child {
    border-top: 1px solid ${theme.vars.newColor.lightGS1};
  }
  &:hover,
  &:focus-within {
    background-color: ${theme.vars.newColor.lightGS2};
  }
  `}
  ${({ variation, theme }) =>
    variation === 'full-border' &&
    `
  border: 1px solid ${theme.vars.newColor.lightGS1};
  margin-bottom: 15px;
  min-height: 70px;
  box-sizing: border-box;
  `}
  ${({ variation, theme, status }) => {
    if (!(variation === 'full-border' && !!status?.message)) {
      return '';
    }
    let colour = theme.vars.newColor.green2;
    if (status?.type === 'error') {
      colour = theme.vars.newColor.red2;
    } else if (status?.type === 'warning') {
      colour = theme.vars.newColor.orange2;
    }

    return `
    &:after {
      content: "";
      height: 1px;
      background-color: ${colour};
      outline: 1px solid ${colour};
      width: 100%;
      display: block;
      position: absolute;
      left: 0;
      bottom: -3px;
      box-sizing: content-box;
    }`;
  }}
  ${({ variation }) =>
    variation === 'clean' &&
    `
    :hover,
    :focus-within {
      box-shadow:0 2px 5px rgba(0, 0, 0, 0.16);
    }
  `}
  ${({ onClick }) =>
    onClick &&
    `
    cursor: pointer;
  `}
`;

const OptionsElement = styled.div<{ hasStatus: boolean }>`
  display: flex;
  justify-content: flex-end;
  ${({ hasStatus }) => !hasStatus && 'margin-left: 30px;'}
  min-width: 40px;
`;

const IconCircle = styled.div`
  display: inline-flex;
  width: 20px;
  height: 20px;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  margin-right: 8px;
`;

const StatusElement = styled.div<{ colour: string }>`
  margin: 0;
  ${({ colour }) => `color: ${colour};`}

  & > ${IconCircle} {
    ${({ colour }) => `background-color: ${colour};`}
    ${({ colour }) =>
      colour !== vars.newColor.green2 &&
      `& > svg {
        margin-bottom: 2px;
      }`}
  }

  span {
    font-size: 1.2rem;
  }
`;

type TagVariant = 'active' | 'inactive';

const Tag = styled.span<{ variant: TagVariant }>`
  padding: 6px 12px;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ variant, theme }) => (variant === 'active' ? theme.vars.color.white : theme.vars.color.grey4)};
  background-color: ${({ variant, theme }) => (variant === 'active' ? theme.vars.color.green : theme.vars.color.grey2)};
  display: inline-block;
  text-align: center;
  white-space: nowrap;
  margin-left: 12px;
`;

const statusHandler = (status: StatusProp, variation: any): void | JSX.Element => {
  if (!status?.message || variation !== 'full-border') {
    return;
  }

  let colour = vars.newColor.green2;

  if (status.type === 'error') {
    colour = vars.newColor.red2;
  } else if (status.type === 'warning') {
    colour = vars.newColor.orange2;
  }

  const icon = status.type !== 'success' ? 'exclamation-triangle' : 'check';

  return (
    <StatusElement colour={colour}>
      <IconCircle>
        <Icon icon={icon} color="white" size="xs" />
      </IconCircle>
      <span>{status.message}</span>
    </StatusElement>
  );
};

export interface StatusProp {
  /**
   * Type of status to show, success green, warning amber, error red
   */
  type?: 'error' | 'success' | 'warning';
  /**
   * Message to display with status, if present an icon relevant to the type will prefix the message
   */
  message: string;
}

interface IProps extends AccessibleRoleProp {
  /**
   * Sets a min-width to the columns
   * @default columnMinWidth: { value: 200, unit: "px" }
   * @deprecated
   */
  columnMinWidth?: IColumnWidthProps;
  /**
   * A slot for the list item options, for example an edit button.
   */
  options?: JSX.Element;
  /**
   * Optional variation, choose from: 'clean' or 'full-border'
   */
  variation?: 'clean' | 'full-border';
  /**
   * Optional status applicable only in conjuntion with variation 'full-border'
   * ```{
   *   message: string,
   *   type: 'success' (default) | 'error' | 'warning',
   * }```
   */
  status?: StatusProp;
  /**
   * Fires an event when the list item clicked.
   */
  onClick?: () => void;
  /**
   * Optional tag to show on the list item
   */
  tag?: {
    label: string;
    variant: TagVariant;
  };
}

export const ListItem: FC<IProps> = props => {
  return (
    <ListItemElement data-testid="ListItem" {...props}>
      <Grid columnMinWidth={props.columnMinWidth} gridGap={20} data-testid="ListItemChildrenGrid">
        {props.children}
        {statusHandler(props.status, props.variation)}
      </Grid>

      <OptionsElement data-testid="ListItemOptions" hasStatus={props.variation === 'full-border' && !!props.status}>
        {props.options}
      </OptionsElement>
      {props.tag && <Tag variant={props.tag.variant}>{props.tag.label}</Tag>}
    </ListItemElement>
  );
};

ListItem.defaultProps = {
  role: 'listitem',
  columnMinWidth: {
    value: 200,
    unit: 'px',
  },
};
